import { message } from "antd";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  generateRefNumber,
  capitalize,
} from "./formatters";

// ============================================
// CORE CSV CONVERTER
// ============================================
const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) return "";

  const headers = columns
    .map((col) => `"${col.label}"`)
    .join(",");

  const rows = data.map((row, index) => {
    return columns
      .map((col) => {
        let value;

        if (col.key === "sno") {
          value = index + 1;
        } else if (
          col.format &&
          typeof col.format === "function"
        ) {
          try {
            value = col.format(row);
          } catch {
            value = "N/A";
          }
        } else {
          value = row[col.key];
        }

        if (
          value === null ||
          value === undefined ||
          value === ""
        ) {
          return '""';
        }

        if (typeof value === "number") return value;

        const strValue = String(value)
          .replace(/"/g, '""')
          .replace(/\n/g, " ");
        return `"${strValue}"`;
      })
      .join(",");
  });

  return [headers, ...rows].join("\n");
};

// ============================================
// MAIN EXPORT FUNCTION - FIXED
// ============================================
export const exportToCSV = (
  data,
  columns,
  filename = "report"
) => {
  try {
    if (!data || data.length === 0) {
      message.warning("No data available to export");
      return false;
    }

    const csv = convertToCSV(data, columns);

    // UTF-8 BOM
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    // ✅ Unique timestamp - har baar naya URL
    const timestamp = Date.now();
    const date = new Date().toISOString().split("T")[0];
    const fullFilename = `${filename}_${date}_${timestamp}.csv`;

    // ✅ Fresh blob URL
    const blobUrl = URL.createObjectURL(blob);

    // ✅ Fresh anchor - har baar naya element
    const anchor = document.createElement("a");
    anchor.style.cssText =
      "display:none;position:fixed;top:-100px;left:-100px;";
    anchor.href = blobUrl;
    anchor.download = fullFilename;

    // ✅ Body mein add
    document.body.appendChild(anchor);

    // ✅ Click
    anchor.click();

    // ✅ Cleanup with delay
    setTimeout(() => {
      if (document.body.contains(anchor)) {
        document.body.removeChild(anchor);
      }
      URL.revokeObjectURL(blobUrl);
    }, 3000);

    message.success(
      `✅ CSV exported (${data.length} records)`
    );
    return true;
  } catch (error) {
    console.error("CSV Export Error:", error);
    message.error("Failed to export CSV");
    return false;
  }
};

// ============================================
// REPORT COLUMNS
// ============================================
export const REPORT_COLUMNS = {
  users: [
    { key: "sno", label: "S.No." },
    {
      key: "profile_id",
      label: "Profile ID",
      format: (r) =>
        r.profile_id ||
        `CR${String(r.id || 1000).padStart(6, "0")}`,
    },
    {
      key: "full_name",
      label: "Full Name",
      format: (row) =>
        capitalize(row.first_name) ||
        capitalize(row.username) ||
        "N/A",
    },
    {
      key: "email",
      label: "Email",
      format: (row) => row.email || "N/A",
    },
    {
      key: "contact",
      label: "Contact",
      format: (row) => row.contact || "N/A",
    },
    {
      key: "gender",
      label: "Gender",
      format: (row) => capitalize(row.gender) || "N/A",
    },
    {
      key: "age",
      label: "Age",
      format: (row) => row.age || "N/A",
    },
    {
      key: "city",
      label: "City",
      format: (row) => row.district || row.city || "N/A",
    },
    {
      key: "state",
      label: "State",
      format: (row) => row.state || "N/A",
    },
    {
      key: "religion",
      label: "Religion",
      format: (row) => capitalize(row.religion) || "N/A",
    },
    {
      key: "status",
      label: "Status",
      format: (row) => (row.is_active ? "Active" : "Inactive"),
    },
    {
      key: "premium",
      label: "Premium",
      format: (row) =>
        row.is_premium || row.premium ? "Yes" : "No",
    },
    {
      key: "joined_date",
      label: "Joined Date",
      format: (row) =>
        formatDate(row.created_date || row.date_joined),
    },
  ],

  staff: [
    { key: "sno", label: "S.No." },
    {
      key: "staff_ref",
      label: "Staff ID",
      format: (row) =>
        generateRefNumber("staff", row.id || 0),
    },
    {
      key: "full_name",
      label: "Full Name",
      format: (row) =>
        capitalize(row.first_name) ||
        capitalize(row.username) ||
        "N/A",
    },
    {
      key: "email",
      label: "Email",
      format: (row) => row.email || "N/A",
    },
    {
      key: "contact",
      label: "Contact",
      format: (row) => row.contact || "N/A",
    },
    {
      key: "city",
      label: "City",
      format: (row) => row.district || row.city || "N/A",
    },
    {
      key: "state",
      label: "State",
      format: (row) => row.state || "N/A",
    },
    {
      key: "status",
      label: "Status",
      format: (row) => (row.is_active ? "Active" : "Inactive"),
    },
    {
      key: "joined_date",
      label: "Joined Date",
      format: (row) =>
        formatDate(row.created_date || row.date_joined),
    },
  ],

  transactions: [
    { key: "sno", label: "S.No." },
    {
      key: "txn_ref",
      label: "Transaction ID",
      format: (row) =>
        generateRefNumber("transaction", row.id || 0),
    },
    {
      key: "user_name",
      label: "User Name",
      format: (row) =>
        row.user_name || row.username || "N/A",
    },
    {
      key: "amount",
      label: "Amount (₹)",
      format: (row) =>
        parseFloat(row.amount || 0).toFixed(2),
    },
    {
      key: "type",
      label: "Type",
      format: (row) => capitalize(row.type) || "N/A",
    },
    {
      key: "payment_method",
      label: "Payment Method",
      format: (row) => row.payment_method || "N/A",
    },
    {
      key: "status",
      label: "Status",
      format: (row) => capitalize(row.status) || "N/A",
    },
    {
      key: "date",
      label: "Date & Time",
      format: (row) =>
        formatDateTime(row.created_date || row.date),
    },
  ],

  revenue: [
    { key: "sno", label: "S.No." },
    {
      key: "rev_ref",
      label: "Revenue ID",
      format: (row) =>
        generateRefNumber("commission", row.id || 0),
    },
    {
      key: "amount",
      label: "Amount (₹)",
      format: (row) =>
        parseFloat(row.admin_amount || 0).toFixed(2),
    },
    {
      key: "source",
      label: "Source",
      format: (row) => row.source || "Commission",
    },
    {
      key: "description",
      label: "Description",
      format: (row) =>
        row.description || "Admin Commission",
    },
    {
      key: "date",
      label: "Date",
      format: (row) =>
        formatDate(row.created_date || row.date),
    },
  ],

  withdrawals: [
    { key: "sno", label: "S.No." },
    {
      key: "wdl_ref",
      label: "Withdrawal ID",
      format: (row) =>
        generateRefNumber("withdrawal", row.id || 0),
    },
    {
      key: "user_name",
      label: "User Name",
      format: (row) =>
        row.user_name || row.username || "N/A",
    },
    {
      key: "amount",
      label: "Amount (₹)",
      format: (row) =>
        parseFloat(row.amount || 0).toFixed(2),
    },
    {
      key: "bank_details",
      label: "Bank / UPI",
      format: (row) =>
        row.bank_account ||
        row.upi_id ||
        row.account_number ||
        "N/A",
    },
    {
      key: "ifsc",
      label: "IFSC Code",
      format: (row) => row.ifsc_code || "N/A",
    },
    {
      key: "status",
      label: "Status",
      format: (row) => capitalize(row.status) || "N/A",
    },
    {
      key: "request_date",
      label: "Request Date",
      format: (row) =>
        formatDate(row.created_date || row.date),
    },
  ],
};